import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Spinner from '../components/common/Spinner'
import { LessonDonePill, LessonDoneTile } from '../components/learning/LessonDone'
import { clock } from '../lib/clock'
import { LEVEL_LABELS, continueTarget, greetingFor, remainingLine, showContinueCard } from '../lib/home'
import { isLessonDone, lessonCardLook } from '../lib/lessonDone'
import { faDigits } from '../lib/numerals'
import { bandOf } from '../lib/scoreBand'
import { classifyFailure } from '../services/api'
import { listHistory, listLessons, myStats } from '../services/lessons'
import { useAuthStore } from '../store/authStore'
import { ContinueCard, StreakPill } from './Home'

function levelFilters(t) {
  return [
    { value: undefined, label: t('levels.all') },
    { value: 'beginner', label: t('levels.beginner') },
    { value: 'intermediate', label: t('levels.intermediate') },
    { value: 'advanced', label: t('levels.advanced') },
  ]
}

/** The level chip's tint, the design's: beginner green, intermediate blue, advanced red. */
const LEVEL_CHIP = {
  beginner: 'bg-good-tint text-good-ink',
  intermediate: 'bg-brand-50 text-brand-700',
  advanced: 'bg-bad-tint text-bad-ink',
}

const SCORE_COLOUR = { good: 'text-good', warn: 'text-warn', bad: 'text-bad' }

function WaveGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M8 8v8M16 8v8M4 11v2M20 11v2" />
    </svg>
  )
}

export function LessonRow({ lesson }) {
  const { t } = useTranslation('lessons')
  // Finished — every sentence has a scored take — not merely "tried once":
  // one take on one sentence already sets lastScore.
  const done = isLessonDone(lesson)
  const raw = done && lesson.lessonScore != null ? lesson.lessonScore : lesson.lastScore
  const scored = raw !== null && raw !== undefined
  const score = scored ? Math.round(raw) : null
  return (
    <li>
      <Link
        to={`/lesson/${lesson.id}`}
        data-lesson-done={done ? 'true' : undefined}
        className={`flex min-h-[76px] items-center gap-[13px] rounded-2xl p-3.5 transition ${lessonCardLook(done)} ${done ? 'hover:brightness-[0.98]' : 'hover:bg-surface-2'}`}
      >
        {done ? (
          <LessonDoneTile />
        ) : (
          <span className="grid size-[46px] shrink-0 place-items-center rounded-[13px] bg-brand-50 text-brand-600">
            <WaveGlyph />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span dir="ltr" className="lesson-text block truncate text-right text-base font-medium text-ink">
            {lesson.titleEn}
          </span>
          {lesson.titleFa && <span className="text-xs text-ink-2">{lesson.titleFa}</span>}
          <span className="mt-0.5 flex items-center gap-2">
            <span className={`rounded-md px-2 py-[3px] text-[10.5px] font-semibold ${done && lesson.level === 'beginner' ? 'bg-surface text-good-ink' : (LEVEL_CHIP[lesson.level] ?? 'bg-surface-2 text-ink-2')}`}>
              {LEVEL_LABELS[lesson.level] ?? lesson.level}
            </span>
            <span className="tnum text-[11px] text-ink-3" dir="ltr">
              {clock(lesson.durationSeconds)}
            </span>
            <span className="text-[10.5px] font-semibold text-ink-3">{t('row.sentenceCount', { count: faDigits(lesson.sentenceCount) })}</span>
          </span>
        </span>
        {done ? (
          <span className="flex shrink-0 flex-col items-center gap-1">
            <LessonDonePill />
            {scored && <span className={`text-[15px] font-bold ${SCORE_COLOUR[bandOf(score)]}`}>{faDigits(score)}</span>}
          </span>
        ) : scored ? (
          <span className="flex shrink-0 flex-col items-center gap-0.5">
            <span className={`text-[17px] font-bold ${SCORE_COLOUR[bandOf(score)]}`}>{faDigits(score)}</span>
            <span className="text-[9.5px] text-ink-3">{t('row.score')}</span>
          </span>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-ink-3" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        )}
      </Link>
    </li>
  )
}

/**
 * The lessons tab (design page 4, `lessons`; P-home splits it out of the
 * old dashboard). The level chips really filter the list and the count
 * follows them; the continue card shows under «همه» and under its own
 * lesson's level only, so a learner browsing beginner lessons is not
 * handed an intermediate one to continue. Rows lead to practice — this
 * client has no `watch` page yet (P-core-1, wave 5).
 */
export default function Lessons() {
  const { t } = useTranslation('lessons')
  const user = useAuthStore((s) => s.user)
  const [lessons, setLessons] = useState(null)
  const [history, setHistory] = useState(null)
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)
  const [level, setLevel] = useState(undefined)

  useEffect(() => {
    let cancelled = false
    setError(null)
    listLessons()
      .then((data) => {
        if (!cancelled) setLessons(data.items)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    listHistory()
      .then((data) => {
        if (!cancelled) setHistory(data.items)
      })
      .catch(() => {
        if (!cancelled) setHistory(false)
      })
    myStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reload])

  const visible = useMemo(() => (lessons ?? []).filter((lesson) => !level || lesson.level === level), [lessons, level])
  const target = lessons && Array.isArray(history) ? continueTarget(lessons, history) : null
  const filters = levelFilters(t)

  return (
    <div className="mx-auto max-w-md space-y-[13px]">
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="truncate text-[23px] font-bold tracking-[-0.3px] text-ink">{greetingFor(user)}</h1>
          <p className="text-[13px] text-ink-2">{lessons ? remainingLine(lessons) : ' '}</p>
        </div>
        {stats && <StreakPill days={stats.streakCurrent ?? 0} />}
      </header>

      <div className="flex flex-wrap gap-[7px]">
        {filters.map((filter) => (
          <button
            key={filter.label}
            type="button"
            aria-pressed={level === filter.value}
            onClick={() => setLevel(filter.value)}
            className={`h-9 rounded-full px-[15px] text-[12.5px] font-semibold transition-colors ${
              level === filter.value ? 'bg-ink text-on-ink' : 'bg-surface text-ink-2 ring-1 ring-line'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && !lessons ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('loadError.offline') : t('loadError.generic')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('loadError.retry')}
          </Button>
        </div>
      ) : !lessons ? (
        <Spinner label={t('loading')} />
      ) : lessons.length === 0 ? (
        <div className="flex flex-col items-center gap-3 card-soft px-6 py-10 text-center">
          <p className="text-[17px] font-bold text-ink">{t('empty.title')}</p>
          <p className="max-w-[260px] text-[12.5px] leading-[1.9] text-ink-2">{t('empty.body')}</p>
        </div>
      ) : (
        <>
          {showContinueCard(target, level) && <ContinueCard target={target} />}

          <div className="flex items-center justify-between px-0.5">
            <span className="text-[12.5px] font-semibold text-ink-2">{t('list.count', { count: faDigits(visible.length) })}</span>
            <span className="text-[11.5px] text-ink-3">{t('list.latest')}</span>
          </div>

          {visible.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-2">{t('list.emptyLevel')}</p>
          ) : (
            <ul className="space-y-2.5">
              {visible.map((lesson) => (
                <LessonRow key={lesson.id} lesson={lesson} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
